import { WallboxConfigurationDto } from 'src/Libs/wallboxes/api'
import { WallboxConfigurationModel, WallboxPhaseType } from 'src/Libs/wallboxes'
import { UNIT_FACTOR_WATT_TO_WATTHOUR } from 'src/Configuration/defaults'

/**
 * Transform WallboxConfigurationDto to WallboxConfigurationModel
 */
const fromSettingsDtoToModelTransformer = (
  dto: WallboxConfigurationDto,
  wallboxStorageId?: number | string,
  wallboxIndex?: number | string
): WallboxConfigurationModel => ({
  /* activeChargeStrategy used only in software version < X_2024_04 */
  activeChargeStrategy: dto.activeChargeStrategy,

  authRequired: Boolean(dto.authRequired),
  autoPhaseSwitchEnabled: Boolean(dto.autoPhaseSwitchEnabled),
  bidiModeActive: Boolean(dto.bidiModeActive),
  bidiShiftModeSocRange: dto.bidiShiftModeSocRange
    ? {
        paramBidiShiftModeStartSoc: Number(dto.bidiShiftModeSocRange?.paramBidiShiftModeStartSoc),
        paramBidiShiftModeEndSoc: Number(dto.bidiShiftModeSocRange?.paramBidiShiftModeEndSoc),
      }
    : undefined,
  /* 0: wallbox limited by current (A); 1: wallbox limited by power (W); undefined: treat as 0; writable: no */
  chargeLimitType: dto?.chargeLimitType,
  chargeStopHysteresis: Number(dto.chargeStopHysteresis),
  ledIntensity: dto.ledIntensity,
  cableLock: dto.cableLock,
  isBoostModeActive: dto.chargeFull,

  /* chargeStrategy, chargeStrategyConfiguration used only in software version >= X_2024_04 */
  chargeStrategy: dto.chargeStrategy,
  chargeStrategyConfiguration: dto.chargeStrategyConfiguration,

  deviceName: dto.deviceName,
  deviceState: {
    connected: Boolean(dto.deviceState?.deviceConnected),
    working: Boolean(dto.deviceState?.deviceWorking),
    inService: Boolean(dto.deviceState?.deviceInService),
  },
  dhcpEnabled: Boolean(dto.dhcpEnabled),
  diagBatCapacity: Number(dto.diagBatCapacity) / UNIT_FACTOR_WATT_TO_WATTHOUR,
  gateway: dto.gateway,
  ipAddr: dto.ipAddr,
  keyLockMode: dto.keyLockMode,
  listIndex: Number(dto.index),
  lowerCurrentLimit: Number(dto.lowerCurrentLimit),
  maxChargeCurrent: Number(dto.maxChargeCurrent),
  maxPowerPerPhase: Number(dto.maxPowerPerPhase),
  minChargeCurrent: Number(dto.minChargeCurrent),

  /* minChargePower, maxChargePower, maxDischargePower are used when chargeLimitType is 1  */
  minChargePower: dto?.minChargePower,
  maxChargePower: dto?.maxChargePower,
  /* Maximum discharge power, total over all phases (only bidirectional wallboxes) */
  maxDischargePower: dto?.maxDischargePower,
  /* Lower bound for minChargePower (W) writable: no */
  lowerChargePowerLimit: dto?.lowerChargePowerLimit,
  /* Upper bound for maxChargePower (W) writable: no */
  upperChargePowerLimit: dto?.upperChargePowerLimit,
  /* Lower bound for maxDischargePower (W) writable: no */
  lowerDischargePowerLimit: dto?.lowerDischargePowerLimit,
  /* Upper bound for maxDischargePower (W) writable: no */
  upperDischargePowerLimit: dto?.upperDischargePowerLimit,

  minPowerPerPhase: Number(dto.minPowerPerPhase),
  numberPhases: dto.numberPhases as WallboxPhaseType,
  pmEnergyL1: Number(dto.pmEnergyL1),
  pmEnergyL2: Number(dto.pmEnergyL2),
  pmEnergyL3: Number(dto.pmEnergyL3),
  pmPowerL1: Number(dto.pmPowerL1),
  pmPowerL2: Number(dto.pmPowerL2),
  pmPowerL3: Number(dto.pmPowerL3),
  rfidReaderEnabled: dto.rfidReaderEnabled,
  subnetMask: dto.subnetMask,
  sunModeActive: Boolean(dto.sunModeActive),
  upperCurrentLimit: Number(dto.upperCurrentLimit),
  wallboxType: dto.wallboxType,
  wallboxIndex: Number(wallboxIndex) || dto.index,
  storageSerialNo: wallboxStorageId?.toString(),
  serialNo: dto.serial,

  /* weekdays used only in software version < X_2024_04 */
  weekdays: dto.parameterList,
})

export default fromSettingsDtoToModelTransformer
